import React from "react";
import { Link } from "react-router-dom";

/**
 * Reusable Button component matching the website's design system
 *
 * @param {string} variant - "primary" (dark bg, orange text), "secondary" (orange bg, dark text), "outline" (transparent with border)
 * @param {string} size - "sm", "md", "lg"
 * @param {string} to - If provided, renders as a React Router Link
 * @param {string} href - If provided, renders as an anchor tag
 * @param {boolean} fullWidth - If true, button takes full container width
 * @param {function} onClick - Click handler
 * @param {string} className - Additional CSS classes
 * @param {React.ReactNode} children - Button content
 */
const Button = ({
  variant = "primary",
  size = "md",
  to,
  href,
  fullWidth = false,
  onClick,
  className = "",
  children,
  disabled = false,
  type = "button",
  ...props
}) => {
  const baseStyles = {
    display: "inline-block",
    fontWeight: 700,
    border: "none",
    borderRadius: "var(--radius-sm)",
    textDecoration: "none",
    textAlign: "center",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all var(--transition-normal)",
    opacity: disabled ? 0.6 : 1,
    width: fullWidth ? "100%" : "auto",
  };

  const sizeStyles = {
    sm: {
      padding: "var(--space-xs) var(--space-sm)",
      fontSize: "14px",
    },
    md: {
      padding: "var(--space-sm) var(--space-lg)",
      fontSize: "18px",
    },
    lg: {
      padding: "var(--space-md) var(--space-xl)",
      fontSize: "20px",
    },
  };

  const variantStyles = {
    primary: {
      backgroundColor: "var(--color-dark)",
      color: "var(--color-primary)",
      boxShadow: "var(--shadow-md)",
    },
    secondary: {
      backgroundColor: "var(--color-primary)",
      color: "var(--color-dark)",
      boxShadow: "var(--shadow-md)",
    },
    outline: {
      backgroundColor: "transparent",
      color: "var(--color-dark)",
      border: "2px solid var(--color-dark)",
      boxShadow: "none",
    },
  };

  const combinedStyles = {
    ...baseStyles,
    ...sizeStyles[size],
    ...variantStyles[variant],
  };

  const handleMouseEnter = (e) => {
    if (disabled) return;
    e.currentTarget.style.transform = "translateY(-3px)";
    e.currentTarget.style.boxShadow = "var(--shadow-lg)";
    if (variant === "primary") {
      e.currentTarget.style.backgroundColor = "#1a1d21";
      e.currentTarget.style.color = "var(--color-white)";
    } else if (variant === "secondary") {
      e.currentTarget.style.backgroundColor = "#d9734a";
      e.currentTarget.style.color = "var(--color-white)";
    } else if (variant === "outline") {
      e.currentTarget.style.backgroundColor = "var(--color-dark)";
      e.currentTarget.style.color = "var(--color-primary)";
    }
  };

  const handleMouseLeave = (e) => {
    if (disabled) return;
    e.currentTarget.style.transform = "translateY(0)";
    e.currentTarget.style.boxShadow = variantStyles[variant].boxShadow;
    e.currentTarget.style.backgroundColor = variantStyles[variant].backgroundColor;
    e.currentTarget.style.color = variantStyles[variant].color;
  };

  const handleMouseDown = (e) => {
    if (disabled) return;
    e.currentTarget.style.transform = "translateY(-1px)";
    e.currentTarget.style.boxShadow = "var(--shadow-sm)";
  };

  const handleMouseUp = (e) => {
    if (disabled) return;
    e.currentTarget.style.transform = "translateY(-3px)";
    e.currentTarget.style.boxShadow = "var(--shadow-lg)";
  };

  const eventHandlers = {
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onMouseDown: handleMouseDown,
    onMouseUp: handleMouseUp,
    onClick: disabled ? undefined : onClick,
  };

  // Render as React Router Link
  if (to) {
    return (
      <Link
        to={to}
        style={combinedStyles}
        className={className}
        {...eventHandlers}
        {...props}
      >
        {children}
      </Link>
    );
  }

  // Render as anchor tag
  if (href) {
    return (
      <a
        href={href}
        style={combinedStyles}
        className={className}
        target="_blank"
        rel="noopener noreferrer"
        {...eventHandlers}
        {...props}
      >
        {children}
      </a>
    );
  }

  // Render as button
  return (
    <button
      type={type}
      style={combinedStyles}
      className={className}
      disabled={disabled}
      {...eventHandlers}
      {...props}
    >
      {children}
    </button>
  );
};

export default Button;
